/*
 * The AI chats event union: the only place that knows which `aichat.*` type string carries which
 * payload (S03 §3–§4). It is hand-written because tygo emits the type strings as constants and the
 * payloads as interfaces but cannot join the two, and it is what makes `reducer.ts` exhaustive — a
 * new event type is a compile error there until it is handled or explicitly ignored. The fixtures the
 * Go side writes for every type are checked against it (`wire/fixtures.test.ts`).
 *
 * Eleven are **durable**: thin payloads of ids, words and revs, kept in the change log and replayed to
 * a reader who was away. Six are **focused-only frames** without an event id. Five of them are the
 * newest picture of something a chat on screen shows — the model's text while it streams, a
 * workflow's agents, one agent's steps, who types, who looks. They reach only the connections that
 * have that chat on screen and are never caught up: the next picture heals a lost one, and a page that
 * opens reads the newest through `GET …/live`. The sixth, a piece of the chat's terminal screen, is
 * uncoalesced: every frame counts, in order, and the page that misses one asks for a keyframe
 * (`terminal/terminalStream.ts`). It changes no slice and is never kept.
 */

import type {
	AccessChanged,
	AgentPicture,
	ChatGone,
	ChatRow,
	ItemHead,
	LandingHead,
	LivePicture,
	LoginUsage,
	MachineLine,
	PreviewLine,
	ReaderState,
	RevisionHead,
	TerminalAttach,
	TerminalInputRequest,
	TerminalOutput,
	TypingEvent,
	TypingRequest,
	Viewers,
	WorkflowPicture,
} from "./wire/aichat.gen.ts"

export type AichatEvent =
	// Durable: a chat's whole row whenever any field of it moves, and at every turn's end.
	| { type: "aichat.chat.changed"; eventId: number; data: ChatRow }
	// Durable, to one person: an act took their ownership of a chat away.
	| { type: "aichat.chat.gone"; eventId: number; data: ChatGone }
	// Durable: an evented item's head — never its words; the page fetches the item by its rev.
	| { type: "aichat.item.new"; eventId: number; data: ItemHead }
	| { type: "aichat.item.changed"; eventId: number; data: ItemHead }
	| { type: "aichat.revision.new"; eventId: number; data: RevisionHead }
	| { type: "aichat.landing.changed"; eventId: number; data: LandingHead }
	// Durable, to one person: their read cursor of one chat.
	| { type: "aichat.reader.state"; eventId: number; data: ReaderState }
	// Durable, to every member who may use AI chats: one Claude login's usage, keyed by its opaque id
	// (never its owner or label), and the worker machine's state word.
	| { type: "aichat.login.changed"; eventId: number; data: LoginUsage }
	| { type: "aichat.machine.changed"; eventId: number; data: MachineLine }
	// Durable: one platform of a chat's container, and the access visitors asked for.
	| { type: "aichat.preview.changed"; eventId: number; data: PreviewLine }
	| { type: "aichat.access.changed"; eventId: number; data: AccessChanged }
	// Focused-only frames.
	| { type: "aichat.live"; data: LivePicture }
	| { type: "aichat.workflow"; data: WorkflowPicture }
	| { type: "aichat.agent"; data: AgentPicture }
	| { type: "aichat.typing"; data: TypingEvent }
	| { type: "aichat.viewers"; data: Viewers }
	// Focused-only and uncoalesced: a piece of the chat's terminal screen, applied by offset.
	| { type: "aichat.terminal.output"; data: TerminalOutput }

/**
 * The module's socket requests: somebody types in a chat's composer (R1b), and the chat's terminal —
 * a page that watches it attaches, and an owner's keys go down as input (S02 §5.2). Every other act
 * of the module is an HTTP route (`api.ts`); these are worth a frame and not worth a request.
 */
export interface AichatRequests {
	"aichat.typing": [TypingRequest, Record<string, never>]
	"aichat.terminal.attach": [TerminalAttach, Record<string, never>]
	"aichat.terminal.input": [TerminalInputRequest, Record<string, never>]
}

declare module "../../core/wire/index.ts" {
	interface ModuleEvents {
		aichat: AichatEvent
	}
	// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- the requests are the parent's
	interface ModuleRequests extends AichatRequests {}
}

/** Every type string the union carries, in the order of the Go constants (`wire/types.go`). */
export const AICHAT_EVENT_TYPES = [
	"aichat.chat.changed",
	"aichat.chat.gone",
	"aichat.item.new",
	"aichat.item.changed",
	"aichat.revision.new",
	"aichat.landing.changed",
	"aichat.reader.state",
	"aichat.login.changed",
	"aichat.machine.changed",
	"aichat.preview.changed",
	"aichat.access.changed",
	"aichat.live",
	"aichat.workflow",
	"aichat.agent",
	"aichat.typing",
	"aichat.viewers",
	"aichat.terminal.output",
] as const satisfies readonly AichatEvent["type"][]

/** One of the seventeen types above. */
export type AichatEventType = AichatEvent["type"]

/**
 * The focused-only frames: what a chat on screen shows now — its pictures and its terminal's screen —
 * never in the change log.
 */
export const AICHAT_FRAME_TYPES = [
	"aichat.live",
	"aichat.workflow",
	"aichat.agent",
	"aichat.typing",
	"aichat.viewers",
	"aichat.terminal.output",
] as const satisfies readonly AichatEventType[]

const KNOWN = new Set<string>(AICHAT_EVENT_TYPES)

/** Whether a wire type is one this module owns, which is what the reducer answers by. */
export function isAichatEventType(type: string): type is AichatEventType {
	return KNOWN.has(type)
}
